import { useQueryClient } from '@tanstack/react-query'
import { AxiosError, AxiosResponse } from 'axios'
import DropdownLoading from 'common/components/DropdownLoading'
import ThaiDatePicker from 'common/components/ThaiDatePicker'
import { fNumber } from 'common/utils/formatNumber'
import {
  getCostKey,
  getCostTransactionsHistoryInfiniteKey,
  getListProductByMedicineKey,
  getMedicineNameKey,
  getMedicinePieChartKey,
  getMedicinesByRangeKey,
  getMedicineSummaryTodayKey,
  getRecentMedicineKey,
  STOCK_ID_KEY,
} from 'common/constants/queryKeys'
import useAnalytics, { AnalyticsEvents, ProductDomain } from 'common/hooks/useAnalytics'
import useMedia from 'common/hooks/useMedia'
import useYupValidationResolver from 'common/hooks/useYupValidationResolver'
import { getDateFromInputDateTime, getMaxTimeFromInputDate } from 'common/utils/date'
import { errorMessageByField } from 'common/utils/hook-form'
import endOfToday from 'date-fns/endOfToday'
import parseISO from 'date-fns/parseISO'
import { useGetUnits } from 'modules/cost/api/queries'
import {
  useMutateCreateMedicine,
  useMutateCreateMedicineByProduct,
  useMutateCreateMedicineName,
  useMutateCreateProductMedical,
  useMutateUpdateMedicine,
  useMutateUpdateMedicineByProduct,
} from 'modules/medicalSupply/api/mutation'
import {
  useGetListProductByMedicine,
  useGetMedicineNames,
  useGetRecentMedicine,
} from 'modules/medicalSupply/api/queries'
import { useSpeciesName } from 'modules/pond/api/selector'
import { normalizeSpeciesName } from 'utils/analytics'
import { useInventoryDisplaySetting } from 'modules/inventory/api/selector'
import { useFindMedicineById } from 'modules/medicalSupply/api/selector'
import { medicalSupplyDateFilterState } from 'modules/medicalSupply/stores/atom'
import BaseRecordDialog from 'modules/pond/components/BaseRecordDialog'
import { selectedCycleIdState } from 'modules/pond/stores/atom'
import { enqueueSnackbar } from 'notistack'
import { ChangeEvent, useEffect, useState, useRef, useMemo } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'
import { useRecoilValue } from 'recoil'
import * as Yup from 'yup'

import CloseIcon from '@mui/icons-material/Close'
import TrashIcon from '@mui/icons-material/DeleteOutline'
import {
  TextField,
  Stack,
  Typography,
  MenuItem,
  Grid,
  Box,
  useTheme,
  ListSubheader,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Autocomplete,
} from '@mui/material'
import { TimePicker } from '@mui/x-date-pickers/TimePicker'

import plusIcon from '@iconify/icons-ant-design/plus'
import { Icon } from '@iconify/react'

import { fDateDashTime } from 'utils/formatTime'

interface MedicalSupplyDialogProps {
  onClose: () => void
  open: boolean
  isEdit?: boolean
}

interface FormDataType {
  date: Date
  time: Date
  name: string
  amount: number | undefined
  unit: string
  unit_id: string
  stockId: string
  selected_unit_id: number | string
}

const MedicalSupplyDialog = ({ onClose, open, isEdit }: MedicalSupplyDialogProps) => {
  const { t } = useTranslation('medicalsupply')
  const theme = useTheme()
  const qc = useQueryClient()
  const isMobile = useMedia('sm')

  const [submitting, setSubmitting] = useState(false)

  const date = useRecoilValue(medicalSupplyDateFilterState)

  const medicineData = useFindMedicineById()
  const { data: units } = useGetUnits()
  const { data: names } = useGetMedicineNames()
  const { data: recentMedicine } = useGetRecentMedicine()

  const [isJustCreated, setIsJustCreated] = useState(false)
  const blockRecentRef = useRef(false)
  const createMedicineByProductMutation = useMutateCreateMedicineByProduct()
  const updateMedicineByProductMutation = useMutateUpdateMedicineByProduct()
  const speciesName = useSpeciesName()
  const { trackEvent } = useAnalytics()

  const [openCreateProduct, setOpenCreateProduct] = useState(false)
  const createProductMutation = useMutateCreateProductMedical()
  const {
    data: products,
    isFetching: isProductLoading,
    refetch: refetchProducts,
  } = useGetListProductByMedicine()

  const {
    showLotNumber: showMedicineLotNumber,
    showRemainingStock: showMedicineRemainingStock,
    enforceStock,
  } = useInventoryDisplaySetting('medicine')

  // The active-products list (used for the dropdown options) never
  // includes soft-deleted stock, but an existing medicine being edited may
  // still reference one. Without this, the Autocomplete would render
  // blank for that medicine even though the record itself carries the
  // (deleted) product's name — inject it back in, grouped separately, so
  // it displays correctly labeled instead of disappearing.
  const editingProduct = isEdit ? medicineData?.product : undefined

  const flatProducts = useMemo(() => {
    const base = (products ?? []).flatMap((warehouse) =>
      warehouse.products.map((product) => ({
        ...product,
        warehouse_name: warehouse.warehouse_name,
      }))
    )
    if (editingProduct?.stock_id && !base.some((p) => p.stock_id === editingProduct.stock_id)) {
      const fallbackUnit = {
        id: 0,
        group_code: '',
        code: '',
        name: medicineData?.unit?.name ?? '',
        abbreviation: medicineData?.unit?.alias ?? '',
        standard_multiplier: 1,
      }
      return [
        ...base,
        {
          id: editingProduct.id,
          name: editingProduct.name,
          stock_id: editingProduct.stock_id,
          stock_quantity: 0,
          lot_number: editingProduct.lot_number,
          unit: medicineData?.unit?.alias ?? '',
          base_unit: fallbackUnit,
          unit_options: [fallbackUnit],
          stock_deleted_at: editingProduct.stock_deleted_at,
          warehouse_name: t('deleted_group'),
        },
      ]
    }
    return base
  }, [products, editingProduct, medicineData, t])

  const productDisplayName = (option: {
    name: string
    unit?: string
    stock_deleted_at?: string | null
  }) => `${option.name} (${option.unit})${option.stock_deleted_at ? ` ${t('deleted_suffix')}` : ''}`

  const schema = Yup.object().shape({
    date: Yup.date(),
    time: Yup.date(),
    stockId: Yup.string().required(t('require.name')),
    amount: Yup.number()
      .typeError(t('require.number'))
      .positive(t('require.number'))
      .required(t('require.number')),
  })

  const resolver = useYupValidationResolver(schema)

  const INITIAL_VALUE: FormDataType = {
    date: new Date(),
    time: new Date(),
    name: '',
    amount: undefined,
    unit: '',
    unit_id: '',
    stockId: '',
    selected_unit_id: '',
  }

  const getProductInfoByProductId = (products: any[], productId: string) => {
    if (!products || !productId) return null
    const selectedProduct = products.flatMap((med) => med.products).find((p) => p.id === productId)
    if (!selectedProduct) return null
    return selectedProduct
  }

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
    watch,
    setValue,
    setError,
    getValues,
  } = useForm<FormDataType>({
    defaultValues: INITIAL_VALUE,
    resolver,
  })

  useEffect(() => {
    if (date) {
      reset((values) => ({
        ...values,
        date: date,
        time: date,
      }))
    }
  }, [date])

  useEffect(() => {
    if (medicineData && isEdit && products?.length) {
      const datetime = parseISO(medicineData.datetime)
      const productInfo = getExactProductInfoForEdit(products, medicineData)

      reset({
        date: datetime,
        time: datetime,
        stockId: productInfo?.stock_id ?? medicineData.product?.stock_id ?? '',
        amount: medicineData.amount,
        selected_unit_id: productInfo?.base_unit?.id ?? '',
        unit_id: medicineData.unit.id.toString(),
        unit: medicineData.unit.alias,
        name: '',
      })
      return
    }

  }, [open, medicineData, isEdit, recentMedicine, products, openCreateProduct])

  const onSuccess = (res: AxiosResponse<any>) => {
    const { status } = res.data
    if (status === 200) {
      enqueueSnackbar(t('common:save.success'), { variant: 'success' })
      setSubmitting(false)
      onClose()
      const { stockId } = getValues()
      reset(INITIAL_VALUE)
      qc.invalidateQueries({ queryKey: getMedicinesByRangeKey.all() })
      qc.invalidateQueries({ queryKey: getMedicinePieChartKey.all() })
      qc.invalidateQueries({ queryKey: getMedicineNameKey.all() })
      qc.invalidateQueries({ queryKey: getRecentMedicineKey.all() })
      qc.invalidateQueries({ queryKey: getMedicineSummaryTodayKey.all() })
      qc.invalidateQueries({ queryKey: getListProductByMedicineKey.all() })
      qc.invalidateQueries({ queryKey: getCostKey.all() })
      qc.invalidateQueries({ queryKey: getCostTransactionsHistoryInfiniteKey.all() })
      if (stockId) qc.invalidateQueries({ queryKey: [...STOCK_ID_KEY, stockId] })
      const normalizedSpecies = normalizeSpeciesName(speciesName)
      trackEvent(
        isEdit ? AnalyticsEvents.medicineRecordUpdated : AnalyticsEvents.medicineRecordCreated,
        normalizedSpecies ? { species: normalizedSpecies } : undefined
      )
    }
  }

  const onSubmit = async (data: FormDataType) => {
    setSubmitting(true)
    const date = getDateFromInputDateTime(data.date, data.time)
    const datetime = fDateDashTime(date)
    const medicName = data.name

    let nameId = (names ?? []).find((el) => el.name === medicName)?.id

    const onError = (error: unknown, _variables: any, _context: unknown) => {
      const { response } = error as AxiosError<{ message?: string }>
      // 409 (เช่น STOCK_NOT_ENOUGH) แสดงผ่าน global axios interceptor อยู่แล้ว
      // (src/core/api/interceptors.ts) ไม่ต้อง handle ซ้ำที่นี่ ไม่งั้นจะเด้ง snackbar ซ้อนกัน 2 อัน
      if (response?.status === 400) {
        setError('time', {
          type: 'server',
          message: t('medicalsupply:duplicate.time'),
        })
      }
    }

    let finalAmount = Number(data.amount)
    const product = flatProducts.find((p) => p.stock_id === data.stockId)

    if (product && product.base_unit && data.selected_unit_id) {
      const baseMultiplier = product.base_unit.standard_multiplier;
      const selectedUnit = product.unit_options?.find(u => u.id === data.selected_unit_id) || product.base_unit;
      const optionMultiplier = selectedUnit.standard_multiplier;

      if (baseMultiplier > 0 && optionMultiplier > 0) {
        finalAmount = finalAmount * (optionMultiplier / baseMultiplier);
      }
    }

    if (isEdit) {
      await updateMedicineByProductMutation
        .mutateAsync(
          {
            medicineId: Number(medicineData?.id),
            body: {
              datetime,
              amount: finalAmount,
              stockId: data.stockId,
            },
          },
          { onSuccess, onError }
        )
        .finally(() => setSubmitting(false))
      return
    } else {
      if (!nameId) {
        try {
          await createMedicineByProductMutation.mutateAsync(
            {
              body: {
                datetime,
                amount: finalAmount,
                stockId: data.stockId,
              },
            },
            { onSuccess, onError }
          )
        } catch (error) {
          setSubmitting(false)
        }
      }
    }
  }

  const onCreateProduct = async () => {
    if (!watch('name') || !watch('unit_id')) return

    createProductMutation.mutateAsync(
      {
        body: {
          medicine_name: watch('name'),
          unit_id: Number(watch('unit_id')),
        },
      },
      {
        onSuccess: async (res) => {
          if (res.status === 200) {
            setIsJustCreated(true)
            blockRecentRef.current = true
            enqueueSnackbar(t('common:save.success'), { variant: 'success' })

            const { data: updatedProducts } = await refetchProducts()

            if (updatedProducts) {
              const productInfo = getProductInfoByProductId(updatedProducts, res.data.data.id)

              if (productInfo) {
                setValue('stockId', productInfo.stock_id, { shouldValidate: true })
                setValue('unit', productInfo.unit)
                if (productInfo.base_unit) setValue('selected_unit_id', productInfo.base_unit.id)

                const selectedUnit = units?.find((u: any) => String(u.id) === String(productInfo.unit.id))
                if (selectedUnit) {
                  setValue('unit_id', selectedUnit.id.toString())
                }

                setValue('name', '')
                setOpenCreateProduct(false)
                setTimeout(() => setIsJustCreated(false), 2000)
              }
            }
            trackEvent(AnalyticsEvents.productCreated, { product_domain: ProductDomain.medicine })
          }
        },
        onError: (err) => {
          console.error('Failed to create product:', err)
        },
      }
    )
  }

  const getExactProductInfoForEdit = (products: any[], medicineData: any) => {
    if (!products || !medicineData) return null
    const flat = products.flatMap((med) =>
      med.products.map((p: any) => ({ ...p, warehouse_name: med.warehouse_name }))
    )
    const exactStockId = medicineData.stock_id ?? medicineData.stockId ?? medicineData.product?.stock_id
    if (exactStockId) {
      const found = flat.find((p) => p.stock_id === exactStockId)
      if (found) return found
    }
    const productId = medicineData.product?.id
    const lotNumber = medicineData.lot_number ?? medicineData.product?.lot_number
    const warehouseName = medicineData.warehouse_name ?? medicineData.warehouse?.name

    const foundByDetails = flat.find((p) => {
      const isIdMatch = p.id === productId
      const isLotMatch = lotNumber ? p.lot_number === lotNumber : true
      const isWarehouseMatch = warehouseName ? p.warehouse_name === warehouseName : true

      return isIdMatch && isLotMatch && isWarehouseMatch
    })

    if (foundByDetails) return foundByDetails
    return flat.find((p) => p.id === productId) || null
  }

  const handleClose = () => {
    blockRecentRef.current = false
    setIsJustCreated(false)
    setOpenCreateProduct(false)
    reset(INITIAL_VALUE)
    onClose()
  }

  const currentSelectedProduct = flatProducts.find((p) => p.stock_id === watch('stockId'))

  const currentAmount = watch('amount')
  const currentSelectedUnitId = watch('selected_unit_id')

  let calculatedDisplayAmount = Number(currentAmount || 0)
  let showResult = false

  if (currentSelectedProduct && currentAmount && currentSelectedUnitId) {
    const baseUnit = currentSelectedProduct.base_unit
    const selectedUnit = currentSelectedProduct.unit_options?.find((u) => u.id === currentSelectedUnitId) || baseUnit

    if (baseUnit?.standard_multiplier > 0 && selectedUnit?.standard_multiplier > 0) {
      calculatedDisplayAmount = Number(currentAmount) * (selectedUnit.standard_multiplier / baseUnit.standard_multiplier)

      if (selectedUnit.id !== baseUnit.id) {
        showResult = true
      }
    }
  }

  return (
    <BaseRecordDialog
      onClose={handleClose}
      open={open}
      formId="record-fill-medicine"
      title={t('record.medicine')}
      isSubmitting={submitting}
      submitButtonLabel={t('common:submit')}
      isShowPondName={false}
    >
      <form id="record-fill-medicine" onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={2}>
          <Stack
            direction={isMobile ? 'column' : 'row'}
            spacing={2}
            alignItems={isMobile ? 'flex-start' : 'center'}
          >
            <Grid item xs={6}>
              <Box sx={{ paddingTop: 1, mb: 1 }}>
                <Typography fontSize={16} fontWeight={600}>
                  <Typography component="span" color="error" sx={{ mr: 0.5 }}>
                    *
                  </Typography>
                  {t('common:date')}
                </Typography>
              </Box>

              <Controller
                name="date"
                control={control}
                render={({ field }) => (
                  <ThaiDatePicker
                    {...field}
                    format="dd/MM/yyyy"
                    maxDate={endOfToday()}
                    slotProps={{
                      textField: {
                        id: 'medicine-date-picker',
                        size: 'small',
                        error: !!errors.date,
                        helperText: errorMessageByField('date', errors),
                      },
                    }}
                    sx={{ width: '220px' }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={6}>
              <Box sx={{ paddingTop: 1, mb: 1 }}>
                <Typography fontSize={16} fontWeight={600}>
                  <Typography component="span" color="error" sx={{ mr: 0.5 }}>
                    *
                  </Typography>
                  {t('time')}
                </Typography>
              </Box>

              <Controller
                name="time"
                control={control}
                render={({ field }) => (
                  <TimePicker
                    {...field}
                    format="HH:mm"
                    views={['hours', 'minutes']}
                    timeSteps={{ minutes: 1 }}
                    maxTime={getMaxTimeFromInputDate(watch('date'))}
                    ampm={false}
                    slotProps={{
                      textField: {
                        id: 'medicine-time-picker',
                        size: 'small',
                        error: !!errors.time,
                        helperText: errorMessageByField('time', errors),
                      },
                    }}
                    sx={{ width: '220px' }}
                  />
                )}
              />
            </Grid>
          </Stack>

          <Stack direction="row" alignItems="center">
            <Grid item xs={12} sx={{ width: '100%' }}>
              <Box sx={{ paddingTop: 1, mb: 1 }}>
                <Grid item xs={12}>
                  <Box sx={{ paddingTop: 1, mb: 1 }}>
                    <Typography fontSize={16} fontWeight={600}>
                      <Typography component="span" color="error" sx={{ mr: 0.5 }}>
                        *
                      </Typography>
                      {t('medicine')}
                    </Typography>
                  </Box>

                  <Controller
                    name="stockId"
                    control={control}
                    render={({ field: { onChange, value, ref } }) => {
                      const selectedOption = flatProducts.find((opt) => opt.stock_id === value) || null

                      return (
                        <Autocomplete
                          options={flatProducts}
                          value={selectedOption}
                          isOptionEqualToValue={(option, value) => option.stock_id === value.stock_id}
                          getOptionDisabled={(option) =>
                            enforceStock && (option.stock_quantity ?? 0) <= 0
                          }
                          disabled={openCreateProduct}
                          loading={isProductLoading}
                          groupBy={(option) => option.warehouse_name}
                          getOptionLabel={productDisplayName}
                          onChange={(_event, newValue) => {
                            if (newValue) {
                              onChange(newValue.stock_id)
                              setValue('unit', newValue.unit)

                              if (newValue.base_unit) {
                                setValue('selected_unit_id', newValue.base_unit.id)
                              } else {
                                setValue('selected_unit_id', '')
                              }
                              setValue('amount', undefined)
                            } else {
                              onChange('')
                              setValue('unit', '')
                              setValue('selected_unit_id', '')
                              setValue('amount', undefined)
                            }
                          }}
                          filterOptions={(options, state) => {
                            const inputValue = state.inputValue.toLowerCase()
                            return options.filter(
                              (opt) =>
                                opt.name.toLowerCase().includes(inputValue) ||
                                (opt.lot_number && opt.lot_number.toLowerCase().includes(inputValue))
                            )
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              inputRef={ref}
                              size="small"
                              placeholder={t('medicine.placeholder', { defaultValue: 'เลือกเวชภัณฑ์' })}
                              error={!!errors.stockId}
                              helperText={errorMessageByField('stockId', errors)}
                              fullWidth
                            />
                          )}
                          renderGroup={(params) => (
                            <li key={params.key}>
                              <Box
                                sx={{
                                  px: 2,
                                  py: 1,
                                  fontSize: 14,
                                  fontWeight: 600,
                                  color: 'text.secondary',
                                  bgcolor: 'background.paper',
                                }}
                              >
                                {params.group}
                              </Box>
                              <ul style={{ padding: 0 }}>{params.children}</ul>
                            </li>
                          )}
                          renderOption={(props, option) => (
                            <li {...props} key={option.stock_id}>
                              <Box
                                sx={{
                                  width: '100%',
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  py: 1,
                                }}
                              >
                                <Box>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Typography fontWeight={700} color="text.primary" fontSize={16}>
                                      {productDisplayName(option)}
                                    </Typography>
                                    {enforceStock && (option.stock_quantity ?? 0) <= 0 && (
                                      <Chip
                                        label={t('out_of_stock', { defaultValue: 'หมด' })}
                                        size="small"
                                        sx={{
                                          height: 20,
                                          fontSize: 11,
                                          color: 'error.main',
                                          bgcolor: 'error.lighter',
                                        }}
                                      />
                                    )}
                                  </Box>
                                  {showMedicineLotNumber && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                                      <Box
                                        sx={{
                                          border: '1px solid #e0e0e0',
                                          borderRadius: 1,
                                          px: 1,
                                          py: 0.25,
                                          mr: 1,
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          bgcolor: 'background.paper',
                                        }}
                                      >
                                        <Typography fontSize={12} color="text.secondary">
                                          {t('lot', { defaultValue: 'ล็อต' })}
                                        </Typography>
                                      </Box>
                                      <Typography fontSize={14} color="text.secondary">
                                        {option.lot_number || '-'}
                                      </Typography>
                                    </Box>
                                  )}
                                </Box>
                                {showMedicineRemainingStock && (
                                  <Typography fontSize={14} color="text.secondary">
                                    {t('remaining', { defaultValue: 'เหลือ' })} {fNumber(option.stock_quantity)} {option.unit || t('piece', { defaultValue: 'ชิ้น' })}
                                  </Typography>
                                )}
                              </Box>
                            </li>
                          )}
                        />
                      )
                    }}
                  />

                  <Box sx={{ mt: 1, display: 'flex', alignItems: 'center' }}>
                    <Button
                      variant="text"
                      onClick={() => setOpenCreateProduct(true)}
                      startIcon={<Icon icon={plusIcon} width={16} height={16} />}
                      sx={{
                        color: '#00a152',
                        fontWeight: 600,
                        padding: '4px 8px',
                        minWidth: 'auto',
                        justifyContent: 'flex-start',
                        '&:hover': {
                          backgroundColor: 'transparent',
                          opacity: 0.8,
                        },
                      }}
                    >
                      {t('add.medicine')}
                    </Button>
                  </Box>

                  <Dialog open={openCreateProduct} onClose={() => setOpenCreateProduct(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3, padding: 1 } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, pb: 1 }}>
                      <Typography fontSize={18} fontWeight="bold">{t('record.medicine')}</Typography>
                      <IconButton onClick={() => setOpenCreateProduct(false)} size="small"><CloseIcon /></IconButton>
                    </Box>
                    <DialogContent sx={{ px: 2, py: 1 }}>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 'bold' }}>
                            <span style={{ color: 'red' }}>*</span> {t('medicine.name')}
                          </Typography>
                          <Controller
                            name="name"
                            control={control}
                            render={({ field }) => (
                              <TextField {...field} placeholder={t('medicine.name.placeholder') || 'ระบุชื่อ'} size="small" error={!!errors.name} helperText={errorMessageByField('name', errors)} sx={{ backgroundColor: 'white', borderRadius: 1 }} fullWidth />
                            )}
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 'bold' }}>
                            <span style={{ color: 'red' }}>*</span> {t('unit')}
                          </Typography>
                          <Controller
                            name="unit_id"
                            control={control}
                            render={({ field }) => (
                              <TextField {...field} select size="small" fullWidth error={!!errors.unit_id} helperText={errorMessageByField('unit_id', errors)} sx={{ backgroundColor: 'white', borderRadius: 1 }} SelectProps={{ displayEmpty: true }}>
                                <MenuItem value="" disabled>เลือกหน่วย</MenuItem>
                                {(units ?? []).map((u: any) => (<MenuItem key={u.id} value={u.id}>{u.name}</MenuItem>))}
                              </TextField>
                            )}
                          />
                        </Grid>

                        {watch('name') && watch('unit_id') && (
                          <Grid item xs={12}>
                            <Box sx={{ mt: 1, p: 1.5, backgroundColor: '#e3f2fd', color: '#1976d2', borderRadius: 1, border: '1px solid #90caf9', fontSize: 14 }}>
                              {t('result')}: {watch('name')} ({units?.find((u: any) => String(u.id) === String(watch('unit_id')))?.abbreviation || ''})
                            </Box>
                          </Grid>
                        )}
                      </Grid>
                    </DialogContent>
                    <DialogActions sx={{ p: 2, pt: 1, gap: 1, justifyContent: 'flex-end' }}>
                      <Button onClick={() => setOpenCreateProduct(false)} variant="outlined" color="inherit" sx={{ borderRadius: 1, fontSize: 16 }}>{t('common:cancel')}</Button>
                      <Button color="primary" variant="contained" onClick={() => onCreateProduct()} sx={{ borderRadius: 1, fontSize: 16, '&:hover': { backgroundColor: 'primary.dark' } }}>{t('common:submit')}</Button>
                    </DialogActions>
                  </Dialog>

                </Grid>
              </Box>
            </Grid>
          </Stack>

          {watch('stockId') !== '' && currentSelectedProduct && (
            <Stack direction="row" alignItems="center">
              <Grid item xs={12} sx={{ width: '100%' }}>
                <Box sx={{ paddingTop: 1, mb: 1 }}>
                  <Typography fontSize={16} fontWeight={600}>
                    <Typography component="span" color="error" sx={{ mr: 0.5 }}>
                      *
                    </Typography>
                    {t('amount')}
                  </Typography>
                </Box>

                <Stack direction="row" spacing={0} sx={{ width: '100%' }}>
                  <Controller
                    name="amount"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        placeholder={t('amount.placeholder')}
                        error={!!errors.amount}
                        helperText={errorMessageByField('amount', errors)}
                        fullWidth
                        sx={{
                          maxWidth: '70%',
                          '& .MuiOutlinedInput-root': {
                            borderTopRightRadius: 0,
                            borderBottomRightRadius: 0,
                          },
                          '& .MuiInputBase-root': {
                            height: 40,
                          },
                        }}
                      />
                    )}
                  />

                  <Controller
                    name="selected_unit_id"
                    control={control}
                    render={({ field }) => {
                      const options = currentSelectedProduct?.unit_options && currentSelectedProduct.unit_options.length > 0
                        ? currentSelectedProduct.unit_options
                        : (currentSelectedProduct?.base_unit ? [currentSelectedProduct.base_unit] : []);

                      return (
                        <TextField
                          {...field}
                          select
                          fullWidth
                          SelectProps={{
                            displayEmpty: true,
                          }}
                          sx={{
                            maxWidth: '30%',
                            backgroundColor: theme.palette.grey[50],
                            '& .MuiOutlinedInput-root': {
                              borderTopLeftRadius: 0,
                              borderBottomLeftRadius: 0,
                            },
                            '& .MuiInputBase-root': {
                              height: 40,
                              color: !field.value ? 'text.secondary' : 'inherit',
                            },
                          }}
                        >
                          <MenuItem value="" disabled>
                            {t('medicine.unit')}
                          </MenuItem>

                          {options.map((u) => (
                            <MenuItem key={u.id} value={u.id}>
                              {u.name || u.abbreviation}
                            </MenuItem>
                          ))}

                          {options.length === 0 && currentSelectedProduct?.unit && (
                            <MenuItem value={currentSelectedProduct.unit}>
                              {currentSelectedProduct.unit}
                            </MenuItem>
                          )}
                        </TextField>
                      );
                    }}
                  />
                </Stack>

                {showResult && (
                  <Typography fontSize={14} color="textSecondary" sx={{ mt: 1 }}>
                    {t('result')} = {calculatedDisplayAmount.toLocaleString('en-US', { maximumFractionDigits: 4 })} {currentSelectedProduct.base_unit?.abbreviation || currentSelectedProduct.base_unit?.name}
                  </Typography>
                )}

              </Grid>
            </Stack>
          )}
        </Stack>
      </form>
    </BaseRecordDialog>
  )
}

export default MedicalSupplyDialog